import { ReactNode, useEffect, useMemo, useState } from 'react';

import { ArrowBack, ArrowForward, Close } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  isMonitorPlanLimitError,
  resolveUpgradeModalTypeFromMessage,
  UpgradeModal,
  UpgradeModalType,
} from 'src/pages/Monitor/components/UpgradeModal';
import { useMonitorLimits } from 'src/pages/Monitor/hooks/useMonitorLimits';
import {
  addFundToWallet,
  checkWalletNameAvailability,
  createMonitoringFundWithWallet,
  FicChildrenResponse,
  getUserWalletsWithFundsCount,
} from 'src/services/wallet';
import { getStrapiValidationMessage } from 'src/utils/getStrapiValidationMessage';
import { useDebounce } from 'src/utils/hooks';

import { ViewWalletButton } from './ViewWalletButton';
import { WalletInfo } from './WalletInfo';
import { FIDCIcon } from '../Icons/FIDCIcon';

const EMPTY_FIC_CHILDREN: FicChildrenResponse['children'] = [];

type UpgradeModalState = {
  open: boolean;
  type: UpgradeModalType;
  maxLimit: number | null;
  currentCount: number;
  attemptedCount?: number;
  apiMessage: string | null;
};

type LimitSnapshot = {
  maxWallets: number | null;
  usageWallets: number;
  maxFunds: number | null;
  currentFundsCount: number;
  fundsToAdd: number;
};

type AddToExistingVariables = {
  walletId: number;
  limitSnapshot: LimitSnapshot;
};

interface AddToWalletModalProps {
  open: boolean;
  onClose: () => void;
  operationId: string;
  operationName: string;
  ficChildrenData?: FicChildrenResponse;
  isLoadingFicChildren: boolean;
  isFicChildrenError: boolean;
  onRetryFicChildren: () => void;
  onSuccess: (message: ReactNode, walletId?: number | null) => void;
}

export function AddToWalletModal({
  open,
  onClose,
  operationId,
  operationName,
  ficChildrenData,
  isLoadingFicChildren,
  isFicChildrenError,
  onRetryFicChildren,
  onSuccess,
}: AddToWalletModalProps) {
  const { auth } = useAuthContext();
  const {
    limits,
    refetchMonitorLimits,
    getUniqueFundsCount,
    validateFundsAddition,
    validateWalletCreation,
  } = useMonitorLimits();
  const queryClient = useQueryClient();
  const [selectedWalletId, setSelectedWalletId] = useState<number | null>(null);
  const [newWalletName, setNewWalletName] = useState('');
  const [step, setStep] = useState(1);
  const [selectedChildOperationIds, setSelectedChildOperationIds] = useState<
    string[]
  >([]);
  const [upgradeModal, setUpgradeModal] = useState<UpgradeModalState>({
    open: false,
    type: 'fund',
    maxLimit: null,
    currentCount: 0,
    apiMessage: null,
  });

  // The endpoint is the only authoritative FIC signal. If it is unavailable,
  // keep the monitoring flow safe by treating the operation as unresolved and
  // explicitly sending no children, rather than letting the backend include all
  // children for a possible FIC.
  const isFic = !isFicChildrenError && ficChildrenData?.isFic === true;
  const children = isFic
    ? ficChildrenData?.children ?? EMPTY_FIC_CHILDREN
    : EMPTY_FIC_CHILDREN;
  const childIds = useMemo(
    () => children.map((child) => child.hashId),
    [children],
  );
  const displayName = operationName.replace(/\s*fidc\s*$/i, '').trim();
  const debouncedNewWalletName = useDebounce(newWalletName, 500);
  const isCreatingNewWallet = newWalletName.trim().length > 0;
  const walletSlotsToAdd = isFic
    ? isCreatingNewWallet
      ? 2
      : selectedWalletId !== null
        ? 1
        : 0
    : isCreatingNewWallet
      ? 1
      : 0;
  const walletSlotsValidation = validateWalletCreation(
    limits.usageWallets,
    false,
    walletSlotsToAdd,
  );
  const canCreateNewWallet = validateWalletCreation(
    limits.usageWallets,
    false,
    isFic ? 2 : 1,
  ).isValid;

  useEffect(() => {
    if (open && isFic) {
      setSelectedChildOperationIds(childIds);
      setStep(1);
    }
  }, [childIds, isFic, open]);

  const { data: wallets = [], isLoading: isLoadingWallets } = useQuery(
    'monitor-user-wallets-with-funds-count',
    () => getUserWalletsWithFundsCount(operationId),
    {
      enabled: open && !isLoadingFicChildren,
      refetchOnWindowFocus: true,
    },
  );

  // Funds limits are per wallet; FIC children do not consume fund slots.
  const currentFundsCount = useMemo(() => {
    if (isCreatingNewWallet || selectedWalletId === null) {
      return 0;
    }

    const selectedWallet = wallets.find(
      (wallet) => wallet.id === selectedWalletId,
    );
    const walletFunds = selectedWallet?.walletFunds ?? [];
    const rootFunds = walletFunds.filter((fund) => !fund.parentWalletFund);

    return getUniqueFundsCount(rootFunds.length > 0 ? rootFunds : walletFunds);
  }, [getUniqueFundsCount, isCreatingNewWallet, selectedWalletId, wallets]);

  const resetState = () => {
    setSelectedWalletId(null);
    setNewWalletName('');
    setSelectedChildOperationIds([]);
    setStep(1);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const invalidateMonitoredQueries = () => {
    queryClient.invalidateQueries('operation-monitored-wallets');
    queryClient.invalidateQueries('monitor-user-wallets-with-funds-count');
    refetchMonitorLimits();
  };

  const { data: walletsWithName, isFetching: isCheckingName } = useQuery(
    ['wallet-exists', debouncedNewWalletName],
    () =>
      checkWalletNameAvailability(debouncedNewWalletName, auth?.user?.id ?? 0),
    {
      enabled:
        open && !!debouncedNewWalletName && debouncedNewWalletName.length >= 3,
      staleTime: 1000 * 60,
    },
  );

  const childOperationIds = isFicChildrenError
    ? []
    : isFic
      ? selectedChildOperationIds
      : undefined;
  // Only the FIC/FIDC root consumes a fund slot (children do not).
  const fundsToAdd = 1;
  const { mutate: addToExisting, isLoading: isAdding } = useMutation({
    mutationFn: ({ walletId }: AddToExistingVariables) =>
      addFundToWallet(walletId, operationId, childOperationIds),
    onSuccess: (_data, { walletId }) => {
      invalidateMonitoredQueries();
      resetState();
      onSuccess(
        <>
          A operação <strong>{displayName}</strong> foi adicionada à carteira
          selecionada.
        </>,
        walletId,
      );
    },
    onError: (error: unknown, { limitSnapshot }) => {
      const message = getStrapiValidationMessage(error as never, '');
      if (isMonitorPlanLimitError(message)) {
        const type = resolveUpgradeModalTypeFromMessage(message);
        setUpgradeModal({
          open: true,
          type,
          maxLimit:
            type === 'fund' ? limitSnapshot.maxFunds : limitSnapshot.maxWallets,
          currentCount:
            type === 'fund'
              ? limitSnapshot.currentFundsCount
              : limitSnapshot.usageWallets,
          attemptedCount:
            type === 'fund' ? limitSnapshot.fundsToAdd : undefined,
          apiMessage: message,
        });
        return;
      }
      toast.error(
        message || 'Não foi possível adicionar a operação à carteira.',
      );
    },
  });

  const { mutate: createWallet, isLoading: isCreating } = useMutation({
    mutationFn: () =>
      createMonitoringFundWithWallet(
        newWalletName.trim(),
        operationId,
        childOperationIds,
      ),
    onSuccess: (data) => {
      invalidateMonitoredQueries();
      const createdWalletId = data?.id ?? null;
      resetState();
      onSuccess(
        <>
          Sua carteira foi criada e a operação <strong>{displayName}</strong>{' '}
          foi adicionada a esta carteira.
        </>,
        createdWalletId,
      );
    },
    onError: (error: unknown) => {
      const message = getStrapiValidationMessage(error as never, '');
      if (isMonitorPlanLimitError(message)) {
        const type = resolveUpgradeModalTypeFromMessage(message);
        setUpgradeModal({
          open: true,
          type,
          maxLimit: type === 'fund' ? limits.maxFunds : limits.maxWallets,
          currentCount:
            type === 'fund' ? currentFundsCount : limits.usageWallets,
          attemptedCount: type === 'fund' ? fundsToAdd : undefined,
          apiMessage: message,
        });
        return;
      }
      toast.error(message || 'Não foi possível criar a carteira.');
    },
  });

  const canSubmit = isCreatingNewWallet || selectedWalletId !== null;
  const isSubmitting = isAdding || isCreating;
  const isNewWalletNameValid =
    newWalletName.trim().length >= 3 && walletsWithName?.length === 0;
  const fundsLimitValidation = validateFundsAddition(
    currentFundsCount,
    fundsToAdd,
  );
  const isBlockedByWalletLimit = !walletSlotsValidation.isValid;
  const isBlockedByFundLimit = !fundsLimitValidation.isValid;
  const isBlockedByLimit = isBlockedByWalletLimit || isBlockedByFundLimit;
  const disableAddButton =
    !canSubmit ||
    isSubmitting ||
    (isCreatingNewWallet && (!isNewWalletNameValid || isCheckingName));
  const allChildrenSelected =
    children.length > 0 && selectedChildOperationIds.length === children.length;

  const openWalletLimitUpgradeModal = () => {
    const max = limits.maxWallets;
    const current = limits.usageWallets;
    setUpgradeModal({
      open: true,
      type: 'wallet',
      maxLimit: max,
      currentCount: current,
      attemptedCount: undefined,
      apiMessage:
        typeof max === 'number'
          ? `Limite de carteiras excedido. Uso atual: ${current} de ${max}. Tentativa de adicionar: ${walletSlotsToAdd || 1}.`
          : null,
    });
  };

  const openLimitUpgradeModal = () => {
    if (isBlockedByWalletLimit) {
      openWalletLimitUpgradeModal();
      return;
    }
    const max = limits.maxFunds;
    const current = currentFundsCount;
    setUpgradeModal({
      open: true,
      type: 'fund',
      maxLimit: max,
      currentCount: current,
      attemptedCount: fundsToAdd,
      apiMessage:
        typeof max === 'number'
          ? `Limite de fundos excedido. Uso atual: ${current} de ${max}. Tentativa de adicionar: ${fundsToAdd}.`
          : null,
    });
  };

  const handleSubmit = () => {
    if (isBlockedByLimit) {
      openLimitUpgradeModal();
      return;
    }

    if (isCreatingNewWallet) {
      createWallet();
    } else if (selectedWalletId !== null) {
      addToExisting({
        walletId: selectedWalletId,
        limitSnapshot: {
          maxWallets: limits.maxWallets,
          usageWallets: limits.usageWallets,
          maxFunds: limits.maxFunds,
          currentFundsCount,
          fundsToAdd,
        },
      });
    }
  };

  const toggleChild = (childId: string) => {
    setSelectedChildOperationIds((selectedIds) =>
      selectedIds.includes(childId)
        ? selectedIds.filter((id) => id !== childId)
        : [...selectedIds, childId],
    );
  };

  const renderOperationIdentity = () => (
    <Box
      display="flex"
      alignItems="center"
      gap={1.5}
      p={1.5}
      mb={2}
      sx={{ bgcolor: 'rgba(255, 130, 17, 0.06)', borderRadius: 2 }}>
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: 'primary.main',
          width: 44,
          height: 44,
          fontSize: '0.9rem',
          fontWeight: 700,
        }}>
        <FIDCIcon />
      </Avatar>
      <Box>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          lineHeight={1.2}
          textTransform="uppercase">
          {displayName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {isFic ? 'FIC' : 'Operação'}
        </Typography>
      </Box>
    </Box>
  );

  const renderFicSelection = () => {
    if (isLoadingFicChildren) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1.5}
          py={4}
          role="status"
          aria-live="polite"
          aria-label="Carregando a composição da operação">
          <CircularProgress
            size="1.75rem"
            aria-label="Carregando a composição da operação"
          />
          <Typography color="text.secondary">
            Carregando a composição da operação...
          </Typography>
        </Box>
      );
    }

    if (!isFic) return null;

    return (
      <>
        {renderOperationIdentity()}
        <Alert severity="info">
          <Typography variant="body2" color="text.secondary">
            O fundo <strong>{displayName}</strong> será adicionado à carteira.
          </Typography>
        </Alert>
        <Typography
          variant="body2"
          color="text.secondary"
          mb={1.5}
          mt={1}
          px={2}>
          Selecione quais fundos da composição você deseja monitorar; a seleção
          pode ser parcial e alterada depois.
        </Typography>
        {children.length === 0 ? (
          <Alert severity="info">
            Esta operação não possui fundos disponíveis para seleção.
          </Alert>
        ) : (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allChildrenSelected}
                  indeterminate={
                    selectedChildOperationIds.length > 0 && !allChildrenSelected
                  }
                  onChange={(_, checked) =>
                    setSelectedChildOperationIds(checked ? childIds : [])
                  }
                />
              }
              label="Selecionar todos"
            />
            <Box
              role="group"
              aria-label="Fundos da operação"
              sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
              {children.map((child) => (
                <Box
                  key={child.hashId}
                  sx={{ borderBottom: 1, borderColor: 'divider', py: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedChildOperationIds.includes(
                          child.hashId,
                        )}
                        onChange={() => toggleChild(child.hashId)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {child.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          CNPJ: {child.cnpj}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </>
    );
  };

  const renderWalletStep = () => (
    <>
      {isFicChildrenError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={onRetryFicChildren}>
              Tentar novamente
            </Button>
          }>
          Não foi possível verificar a composição da operação. Você pode
          continuar, mas nenhum fundo da composição será incluído.
        </Alert>
      )}
      {renderOperationIdentity()}
      {isFic && isCreatingNewWallet && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Este FIC FIDC consumirá <strong>2 créditos de carteira</strong> da
          assinatura (1 pela nova carteira + 1 pelo FIC).
        </Alert>
      )}
      {isFic && !isCreatingNewWallet && selectedWalletId !== null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Este FIC FIDC consome <strong>1 crédito de carteira</strong> da
          assinatura, e não a quantidade de fundos da composição.
        </Alert>
      )}
      <Box
        mb={2}
        px={2}
        py={1.5}
        sx={{
          borderLeft: 4,
          borderColor: 'primary.main',
          borderRadius: '0 8px 8px 0',
          bgcolor: 'action.hover',
        }}>
        <Typography variant="body2" color="text.secondary">
          Adicionar uma operação ao Monitor permite acompanhar indicadores,
          alertas e histórico em um painel centralizado. Organize as operações
          em carteiras por estratégia ou perfil de risco.
        </Typography>
      </Box>

      <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
        Onde deseja adicionar esta operação?
      </Typography>
      <Typography variant="caption" fontWeight={600} color="text.secondary">
        Carteiras existentes
      </Typography>
      <Box
        sx={{
          maxHeight: 196,
          overflowY: 'auto',
          mt: 1,
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pr: 0.5,
        }}>
        {isLoadingWallets ? (
          <Box
            display="flex"
            justifyContent="center"
            py={3}
            role="status"
            aria-live="polite"
            aria-label="Carregando carteiras">
            <CircularProgress
              size="1.5rem"
              color="primary"
              aria-label="Carregando carteiras"
            />
          </Box>
        ) : wallets.length === 0 ? (
          <Typography variant="body2" color="text.secondary" py={1}>
            Você ainda não possui carteiras. Crie uma nova abaixo.
          </Typography>
        ) : (
          wallets.map((wallet) => {
            const selected = selectedWalletId === wallet.id;
            return (
              <Box
                key={wallet.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WalletInfo
                  wallet={wallet}
                  selected={selected}
                  onSelect={() => {
                    setSelectedWalletId(wallet.id);
                    setNewWalletName('');
                  }}
                />
                <ViewWalletButton walletId={wallet.id} />
              </Box>
            );
          })
        )}
      </Box>

      <Typography variant="caption" fontWeight={600} color="text.secondary">
        Nova carteira
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="Digite o nome da nova carteira"
        value={newWalletName}
        disabled={!canCreateNewWallet}
        error={!!(newWalletName.length >= 3 && walletsWithName?.length > 0)}
        helperText={
          !canCreateNewWallet
            ? isFic
              ? 'Sua assinatura não tem capacidade disponível suficiente para monitorar este FIC em uma nova carteira. Faça upgrade para aumentar sua capacidade de monitoramento.'
              : 'Limite de carteiras da assinatura atingido. Solicite upgrade para monitorar outra operação.'
            : newWalletName.length >= 3 && walletsWithName?.length > 0
              ? 'Já existe uma carteira com esse nome'
              : ''
        }
        onChange={(event) => {
          setNewWalletName(event.target.value);
          if (event.target.value) setSelectedWalletId(null);
        }}
        onClick={() => {
          if (!canCreateNewWallet) openWalletLimitUpgradeModal();
        }}
        sx={{ mt: 1 }}
      />
      {!canCreateNewWallet && (
        <Button
          size="small"
          color="primary"
          onClick={openWalletLimitUpgradeModal}
          sx={{ mt: 0.5, alignSelf: 'flex-start' }}>
          Ver opções de upgrade
        </Button>
      )}
    </>
  );

  const isFicSelectionStep = isFic && step === 1;
  const showFicLookupState = isLoadingFicChildren;

  const handleUpgradeModalClose = () => {
    setUpgradeModal({
      open: false,
      type: 'fund',
      maxLimit: null,
      currentCount: 0,
      attemptedCount: undefined,
      apiMessage: null,
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Monitorar operação{isFic ? ` — ${step} de 2` : ''}
          <IconButton
            aria-label="Fechar"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 12,
              top: 12,
              color: theme.palette.grey[500],
            })}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {showFicLookupState || isFicSelectionStep
            ? renderFicSelection()
            : renderWalletStep()}
        </DialogContent>

        <DialogActions
          disableSpacing
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            gap: 1.5,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: 'flex-end',
          }}>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              bgcolor: 'grey.200',
              color: 'text.primary',
              boxShadow: 'none',
              '&:hover': { bgcolor: 'grey.300', boxShadow: 'none' },
            }}>
            Cancelar
          </Button>
          {isFicSelectionStep ? (
            <Button
              variant="outlined"
              onClick={() => setStep(2)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
              endIcon={<ArrowForward fontSize="small" />}>
              Continuar
            </Button>
          ) : !showFicLookupState ? (
            <>
              {isFic && (
                <Button
                  variant="text"
                  onClick={() => setStep(1)}
                  startIcon={<ArrowBack fontSize="small" />}>
                  Voltar
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSubmit}
                disabled={disableAddButton}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
                endIcon={
                  isSubmitting ? (
                    <CircularProgress
                      size="1rem"
                      color="inherit"
                      aria-label="Adicionando operação à carteira"
                    />
                  ) : (
                    <ArrowForward fontSize="small" />
                  )
                }>
                Adicionar
              </Button>
            </>
          ) : null}
        </DialogActions>
      </Dialog>

      <UpgradeModal
        open={upgradeModal.open}
        onClose={handleUpgradeModalClose}
        type={upgradeModal.type}
        maxLimit={upgradeModal.maxLimit}
        currentCount={upgradeModal.currentCount}
        attemptedCount={upgradeModal.attemptedCount}
        apiMessage={upgradeModal.apiMessage}
        userName={auth?.user?.name}
        userId={auth?.user?.id}
      />
    </>
  );
}
